import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, 
  MapPin, AlertTriangle, CheckCircle2, Clock, PlayCircle,
  Filter, RefreshCw, Building2, X
} from "lucide-react";

// Helper to get lifecycle status — trust the backend-computed field (same pattern as Missions.js),
// not a client-side recomputation.
const getLifecycleStatus = (mission) => mission.lifecycle_status || "draft";

// Calendar utility functions
const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

const MissionCalendar = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [missions, setMissions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayModal, setShowDayModal] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    organization: "all",
    lifecycle: "all",
    readiness: "all"
  });
  const [showFilters, setShowFilters] = useState(false);

  // Month names
  const monthNames = {
    en: ["January", "February", "March", "April", "May", "June", 
         "July", "August", "September", "October", "November", "December"],
    cs: ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen",
         "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"]
  };
  
  // Day names
  const dayNames = {
    en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    cs: ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"]
  };

  const lang = t.lang || "en";
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  // Fetch data callback
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [missionsRes, orgsRes] = await Promise.all([
        api.get('/missions'),
        api.get('/organizations')
      ]);
      
      setMissions(missionsRes.data);
      setOrganizations(orgsRes.data);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch missions
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter missions
  const filteredMissions = useMemo(() => {
    return missions.filter(mission => {
      if (filters.organization !== "all" && mission.organization_id !== filters.organization) {
        return false;
      }
      const lifecycle = getLifecycleStatus(mission);
      if (filters.lifecycle !== "all" && lifecycle !== filters.lifecycle) {
        return false;
      }
      if (filters.readiness === "ready" && !mission.compliance_flag) {
        return false;
      }
      if (filters.readiness === "issues" && mission.compliance_flag) {
        return false;
      }
      return true;
    });
  }, [missions, filters]);

  // Get missions for a specific date
  const getMissionsForDate = (date) => {
    // Build the date string from local Y/M/D components, not date.toISOString() — that converts
    // through UTC, and for any timezone ahead of UTC (e.g. CEST) a local midnight rolls back to the
    // previous day, shifting every mission in the grid one day later than its real mission_date.
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return filteredMissions.filter(m => {
      const missionDate = m.mission_date?.split('T')[0];
      return missionDate === dateStr;
    });
  };

  // Navigation
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };
  
  const goToNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };
  
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get organization name
  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.name || "—";
  };

  // Lifecycle badge - Theme-aware colors
  const getLifecycleBadge = (status) => {
    const styles = {
      draft: "bg-[var(--text-muted)]/15 text-[var(--text-muted)] border-[var(--text-muted)]/30",
      ready: "bg-[var(--status-success)]/15 text-[var(--status-success)] border-[var(--status-success)]/30",
      active: "bg-[var(--status-active)]/15 text-[var(--status-active)] border-[var(--status-active)]/30",
      completed: "bg-[var(--brand-accent)]/15 text-[var(--brand-accent)] border-[var(--brand-accent)]/30"
    };
    const labels = {
      draft: t.lifecycle?.draft || "Draft",
      ready: t.lifecycle?.ready || "Ready",
      active: t.lifecycle?.active || "Active",
      completed: t.lifecycle?.completed || "Completed"
    };
    return (
      <Badge variant="outline" className={`text-xs px-1.5 py-0 ${styles[status]}`}>
        {labels[status]}
      </Badge>
    );
  };

  // Build calendar grid
  const buildCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const days = [];
    
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);
    
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(prevYear, prevMonth, daysInPrevMonth - i),
        isCurrentMonth: false
      });
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        date: new Date(currentYear, currentMonth, day),
        isCurrentMonth: true
      });
    }
    
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
      const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
      days.push({
        date: new Date(nextYear, nextMonth, day),
        isCurrentMonth: false
      });
    }
    
    return days;
  };

  const calendarDays = buildCalendarDays();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Handle day click
  const handleDayClick = (dayInfo) => {
    const dayMissions = getMissionsForDate(dayInfo.date);
    if (dayMissions.length > 0) {
      setSelectedDay({ date: dayInfo.date, missions: dayMissions });
      setShowDayModal(true);
    }
  };

  // Render mission item in calendar cell - AEROWAY colors
  const renderMissionItem = (mission, compact = false) => {
    const lifecycle = getLifecycleStatus(mission);
    const hasIssues = !mission.compliance_flag || !mission.pilot_id || !mission.aircraft_id;
    
    const bgColors = {
      draft: "bg-[var(--text-muted)]/10 border-[var(--text-muted)]/30",
      ready: "bg-[var(--status-success)]/10 border-[var(--status-success)]/30",
      active: "bg-[var(--status-active)]/10 border-[var(--status-active)]/30",
      completed: "bg-[var(--brand-accent)]/10 border-[var(--brand-accent)]/30"
    };
    
    if (compact) {
      return (
        <div 
          key={mission.id}
          className={`text-xs px-1.5 py-0.5 rounded border cursor-pointer hover:opacity-80 transition-opacity ${bgColors[lifecycle]} ${hasIssues && lifecycle !== 'completed' ? 'border-l-2 border-l-[var(--status-warning)]' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/missions/${mission.id}`);
          }}
          title={`${mission.mission_id} - ${mission.location}`}
        >
          <div className="flex items-center gap-1 min-w-0">
            <span className="font-medium truncate text-[var(--text-primary)]">{mission.mission_id?.split('-').slice(-1)[0]}</span>
            {hasIssues && lifecycle !== 'completed' && (
              <AlertTriangle className="h-2.5 w-2.5 text-[var(--status-warning)] flex-shrink-0" />
            )}
          </div>
        </div>
      );
    }
    
    return (
      <div 
        key={mission.id}
        className={`p-2 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${bgColors[lifecycle]}`}
        onClick={() => navigate(`/missions/${mission.id}`)}
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <span className="font-medium text-sm text-[var(--text-primary)]">{mission.mission_id}</span>
          {getLifecycleBadge(lifecycle)}
        </div>
        <div className="flex items-center gap-1 text-xs text-[var(--text-muted)] mb-1">
          <MapPin className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{mission.location || "—"}</span>
        </div>
        {hasIssues && lifecycle !== 'completed' && (
          <div className="flex items-center gap-1 text-xs text-[var(--status-warning)]">
            <AlertTriangle className="h-3 w-3" />
            <span>{t.dailyOps?.needsAttention || "Needs attention"}</span>
          </div>
        )}
      </div>
    );
  };

  // Active filter count
  const activeFilterCount = [
    filters.organization !== "all",
    filters.lifecycle !== "all",
    filters.readiness !== "all"
  ].filter(Boolean).length;

  return (
    <div className="space-y-4" data-testid="mission-calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-[var(--text-primary)]">
            {t.missionCalendar?.title || "Mission Calendar"}
          </h1>
          <p className="text-sm text-[var(--text-muted)]">
            {t.missionCalendar?.subtitle || "Visual overview of scheduled missions"}
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={`gap-1.5 ${activeFilterCount > 0 ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)]/10' : ''}`}
          >
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">{t.common?.filters || "Filters"}</span>
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">{activeFilterCount}</Badge>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={goToToday} className="gap-1.5">
            <CalendarIcon className="h-4 w-4" />
            <span className="hidden sm:inline">{t.missionCalendar?.today || "Today"}</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.location.reload()} className="gap-1.5">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="border-[var(--brand-accent)]/30 bg-[var(--brand-accent)]/5">
          <CardContent className="py-3 px-4">
            <div className="flex flex-wrap items-center gap-3">
              {user?.role === "platform_admin" && (
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-[var(--text-muted)]" />
                  <Select
                    value={filters.organization}
                    onValueChange={(v) => setFilters(f => ({ ...f, organization: v }))}
                  >
                    <SelectTrigger className="h-8 w-[160px] text-sm bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--text-primary)]">
                      <SelectValue placeholder={t.common?.organization || "Organization"} />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--card-bg)] border-[var(--card-border)]">
                      <SelectItem value="all">{t.common?.all || "All"}</SelectItem>
                      {organizations.map(org => (
                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-[var(--text-muted)]" />
                <Select
                  value={filters.lifecycle}
                  onValueChange={(v) => setFilters(f => ({ ...f, lifecycle: v }))}
                >
                  <SelectTrigger className="h-8 w-[130px] text-sm bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--text-primary)]">
                    <SelectValue placeholder={t.lifecycle?.status || "Status"} />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--card-bg)] border-[var(--card-border)]">
                    <SelectItem value="all">{t.common?.all || "All"}</SelectItem>
                    <SelectItem value="draft">{t.lifecycle?.draft || "Draft"}</SelectItem>
                    <SelectItem value="ready">{t.lifecycle?.ready || "Ready"}</SelectItem>
                    <SelectItem value="active">{t.lifecycle?.active || "Active"}</SelectItem>
                    <SelectItem value="completed">{t.lifecycle?.completed || "Completed"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[var(--text-muted)]" />
                <Select
                  value={filters.readiness}
                  onValueChange={(v) => setFilters(f => ({ ...f, readiness: v }))}
                >
                  <SelectTrigger className="h-8 w-[130px] text-sm bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--text-primary)]">
                    <SelectValue placeholder={t.flightOps?.readiness || "Readiness"} />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--card-bg)] border-[var(--card-border)]">
                    <SelectItem value="all">{t.common?.all || "All"}</SelectItem>
                    <SelectItem value="ready">{t.missionStatus?.compliant || "Compliant"}</SelectItem>
                    <SelectItem value="issues">{t.missionStatus?.nonCompliant || "Non-Compliant"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFilters({ organization: "all", lifecycle: "all", readiness: "all" })}
                  className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                >
                  <X className="h-4 w-4 mr-1" />
                  {t.common?.clear || "Clear"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendar */}
      <Card>
        <CardHeader className="pb-2 border-b border-[var(--card-border)]">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={goToPreviousMonth}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <CardTitle className="text-lg font-semibold text-[var(--text-primary)]">
              {monthNames[lang]?.[currentMonth] || monthNames.en[currentMonth]} {currentYear}
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={goToNextMonth}>
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-2 lg:p-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="h-6 w-6 animate-spin text-[var(--text-muted)]" />
            </div>
          ) : (
            <>
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {(dayNames[lang] || dayNames.en).map((day, idx) => (
                  <div 
                    key={`day-header-${day}`} 
                    className="text-center text-xs font-medium text-[var(--text-muted)] py-2"
                  >
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((dayInfo, idx) => {
                  const dayMissions = getMissionsForDate(dayInfo.date);
                  const isToday = dayInfo.date.getTime() === today.getTime();
                  const hasReadyMissions = dayMissions.some(m => getLifecycleStatus(m) === 'ready');
                  const hasActiveMissions = dayMissions.some(m => getLifecycleStatus(m) === 'active');
                  const hasIssues = dayMissions.some(m => !m.compliance_flag && getLifecycleStatus(m) !== 'completed');
                  
                  return (
                    <div
                      key={`cal-day-${dayInfo.date.toISOString().split('T')[0]}`}
                      className={`
                        min-h-[80px] lg:min-h-[100px] p-1 lg:p-1.5 rounded-lg border transition-colors cursor-pointer
                        ${dayInfo.isCurrentMonth ? 'bg-[var(--card-bg)]' : 'bg-[var(--bg-main)]/50'}
                        ${isToday ? 'border-[var(--brand-accent)] border-2' : 'border-[var(--border-default)]'}
                        ${dayMissions.length > 0 ? 'hover:border-[var(--border-hover)] hover:shadow-sm' : ''}
                      `}
                      onClick={() => handleDayClick(dayInfo)}
                    >
                      {/* Day number */}
                      <div className="flex items-center justify-between mb-0.5 lg:mb-1">
                        <span className={`
                          text-xs lg:text-sm font-medium
                          ${!dayInfo.isCurrentMonth ? 'text-[var(--text-muted)]/50' : isToday ? 'text-[var(--brand-accent)]' : 'text-[var(--text-secondary)]'}
                        `}>
                          {dayInfo.date.getDate()}
                        </span>
                        {dayMissions.length > 0 && (
                          <div className="flex items-center gap-0.5">
                            {hasActiveMissions && <div className="w-1.5 h-1.5 rounded-full bg-[var(--status-active)]" />}
                            {hasReadyMissions && !hasActiveMissions && <div className="w-1.5 h-1.5 rounded-full bg-[var(--status-success)]" />}
                            {hasIssues && <div className="w-1.5 h-1.5 rounded-full bg-[var(--status-warning)]" />}
                          </div>
                        )}
                      </div>
                      
                      {/* Mission items */}
                      <div className="space-y-0.5">
                        {dayMissions.slice(0, 2).map(mission => renderMissionItem(mission, true))}
                        {dayMissions.length > 2 && (
                          <div className="text-xs text-[var(--text-muted)] px-1">
                            +{dayMissions.length - 2} {t.missionCalendar?.more || "more"}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--text-muted)] px-1">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-[var(--text-muted)]/15 border border-[var(--text-muted)]/30" />
          <span>{t.lifecycle?.draft || "Draft"}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-[var(--status-success)]/15 border border-[var(--status-success)]/30" />
          <span>{t.lifecycle?.ready || "Ready"}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-[var(--status-active)]/15 border border-[var(--status-active)]/30" />
          <span>{t.lifecycle?.active || "Active"}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-[var(--brand-accent)]/15 border border-[var(--brand-accent)]/30" />
          <span>{t.lifecycle?.completed || "Completed"}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3 text-[var(--status-warning)]" />
          <span>{t.dailyOps?.needsAttention || "Needs attention"}</span>
        </div>
      </div>

      {/* Day Detail Modal */}
      <Dialog open={showDayModal} onOpenChange={setShowDayModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col bg-[var(--card-bg)] border-[var(--card-border)]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[var(--text-primary)]">
              <CalendarIcon className="h-5 w-5 text-[var(--brand-accent)]" />
              {selectedDay && (
                <span>
                  {selectedDay.date.toLocaleDateString(lang === 'cs' ? 'cs-CZ' : 'en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 py-2">
            {selectedDay?.missions.map(mission => renderMissionItem(mission, false))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { MissionCalendar };
export default MissionCalendar;
